import { jsPDF } from 'jspdf';
import { TrainingWeekResponse } from '../services/trainingService';

type RGB = [number, number, number];

const SPORT_LABELS: Record<string, string> = {
    run: 'Running',
    swim: 'Swimming',
    bike: 'Cycling',
    weights: 'Strength',
    other: 'Other',
};

const SPORT_COLORS: Record<string, RGB> = {
    run: [239, 68, 68],
    swim: [59, 130, 246],
    bike: [34, 197, 94],
    weights: [168, 85, 247],
    other: [107, 114, 128],
};

// Fallback palette + label for admin-created categories with no hardcoded entry,
// so they still print a coloured chip instead of being dropped.
const FALLBACK_COLORS: RGB[] = [
    [16, 185, 129],
    [244, 63, 94],
    [14, 165, 233],
    [245, 158, 11],
    [99, 102, 241],
    [20, 184, 166],
];

const sportHash = (sport: string): number => {
    let hash = 0;
    for (let i = 0; i < sport.length; i++) hash = (hash * 31 + sport.charCodeAt(i)) & 0xffff;
    return hash;
};

const sportLabel = (sport: string): string =>
    SPORT_LABELS[sport] ?? sport.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const sportColor = (sport: string): RGB =>
    SPORT_COLORS[sport] ?? FALLBACK_COLORS[sportHash(sport) % FALLBACK_COLORS.length];

const CYAN: RGB = [6, 182, 212];
const DARK: RGB = [15, 23, 42];
const MUTED: RGB = [100, 116, 139];
const WHITE: RGB = [255, 255, 255];
const LIGHT_BG: RGB = [248, 250, 252];
const ALT_BG: RGB = [240, 249, 255];
const BORDER: RGB = [226, 232, 240];

function formatDate(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateShort(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export function generateTrainingPDF(weekData: TrainingWeekResponse): void {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const PAGE_W = 210;
    const PAGE_H = 297;
    const MARGIN = 14;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    let y = 0;

    // ── HEADER ───────────────────────────────────────────────────────────────
    doc.setFillColor(...CYAN);
    doc.rect(0, 0, PAGE_W, 36, 'F');

    // Gradient overlay (darker stripe at bottom of header)
    doc.setFillColor(2, 132, 199);
    doc.rect(0, 30, PAGE_W, 6, 'F');

    doc.setTextColor(...WHITE);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('WEEKLY TRAINING PLAN', MARGIN, 14);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const athleteName = `${weekData.user.firstName} ${weekData.user.lastName}`;
    const weekRange = `${formatDate(weekData.weekStart)} — ${formatDate(weekData.weekEnd)}`;
    doc.text(`${athleteName}  ·  ${weekRange}`, MARGIN, 23);

    // Small brand label top-right
    doc.setFontSize(7);
    doc.text('ENJOY TRIATHLON', PAGE_W - MARGIN, 10, { align: 'right' });

    y = 44;

    // ── STATS BAR ─────────────────────────────────────────────────────────────
    const stats = weekData.stats;
    doc.setFillColor(...LIGHT_BG);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(MARGIN, y, CONTENT_W, 16, 2, 2, 'FD');

    const statItems = [
        { label: 'Total Sessions', value: String(stats.totalSessions) },
        { label: 'Completed', value: String(stats.completedSessions) },
        { label: 'Upcoming', value: String(stats.upcomingSessions) },
        {
            label: 'Rest Day',
            value: weekData.user.restDay
                ? weekData.user.restDay.charAt(0).toUpperCase() + weekData.user.restDay.slice(1)
                : '—',
        },
    ];

    const statW = CONTENT_W / statItems.length;
    statItems.forEach((stat, i) => {
        const sx = MARGIN + statW * i + statW / 2;
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...CYAN);
        doc.text(stat.value, sx, y + 8, { align: 'center' });

        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...MUTED);
        doc.text(stat.label.toUpperCase(), sx, y + 13, { align: 'center' });
    });

    y += 22;

    // ── COLUMN HEADERS ────────────────────────────────────────────────────────
    doc.setFillColor(30, 41, 59);
    doc.rect(MARGIN, y, CONTENT_W, 7, 'F');
    doc.setTextColor(...WHITE);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.text('DAY', MARGIN + 3, y + 4.5);
    doc.text('DATE', MARGIN + 28, y + 4.5);
    doc.text('SPORT', MARGIN + 52, y + 4.5);
    doc.text('EXERCISE DETAILS', MARGIN + 82, y + 4.5);
    y += 10;

    // ── DAY ROWS ─────────────────────────────────────────────────────────────
    let rowIndex = 0;
    weekData.days.forEach((day) => {
        // Render one row per entry, or one placeholder row for empty/rest days.
        const sessions: Array<typeof day.entries[number] | null> =
            day.entries.length > 0 ? day.entries : [null];

        sessions.forEach((entry, sessionIdx) => {
            const isAlt = rowIndex % 2 === 1;
            const isFirstSessionOfDay = sessionIdx === 0;
            rowIndex += 1;

            // Calculate row height based on description length
            let descLines: string[] = [];
            const commentLines: string[] = [];
            const descColW = CONTENT_W - 68;

            if (entry?.description) {
                descLines = doc.splitTextToSize(entry.description, descColW);
            }

        const rowHeight = Math.max(
            12,
            8 + descLines.length * 4.5 + (commentLines.length > 0 ? commentLines.length * 4 + 2 : 0),
        );

        // Row background
        doc.setFillColor(...(isAlt ? ALT_BG : (WHITE as RGB)));
        doc.rect(MARGIN, y, CONTENT_W, rowHeight, 'F');

        // Left border accent (cyan for active days, gray for rest/empty)
        const hasEntry = !!entry && !day.isRestDay;
        doc.setFillColor(...(hasEntry ? CYAN : ([203, 213, 225] as RGB)));
        doc.rect(MARGIN, y, 1.5, rowHeight, 'F');

        const textY = y + 5.5;

        // Day name (only on first row for a given day)
        if (isFirstSessionOfDay) {
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...(day.isToday ? (CYAN as RGB) : (DARK as RGB)));
            doc.text(day.dayName.slice(0, 3).toUpperCase(), MARGIN + 4, textY);

            if (day.isPast) {
                doc.setFontSize(5.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...MUTED);
                doc.text('past', MARGIN + 4, textY + 4.5);
            }

            // Date
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...DARK);
            doc.text(formatDateShort(day.date), MARGIN + 28, textY);
        }

        // Sport badge or status
        if (day.isRestDay) {
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...MUTED);
            doc.text('REST DAY', MARGIN + 52, textY);
        } else if (entry?.sport) {
            const chipColor = sportColor(entry.sport);
            doc.setFillColor(...chipColor);
            doc.setDrawColor(...chipColor);
            doc.roundedRect(MARGIN + 51, y + 2, 24, 6, 1.5, 1.5, 'F');
            doc.setTextColor(...WHITE);
            doc.setFontSize(6);
            doc.setFont('helvetica', 'bold');
            doc.text(sportLabel(entry.sport).toUpperCase(), MARGIN + 63, y + 5.8, { align: 'center' });
        } else if (!entry) {
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...MUTED);
            doc.text('No exercise', MARGIN + 52, textY);
        }

        // Description
        if (entry?.description) {
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...DARK);
            doc.text(descLines, MARGIN + 82, textY);
        } else if (entry && !entry.description) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(...MUTED);
            doc.text('No description', MARGIN + 82, textY);
        }

        // Activity link indicator
        if (entry?.activityLink) {
            doc.setFontSize(6);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...CYAN);
            doc.text('▶ Activity link attached', MARGIN + 82, textY + descLines.length * 4.5 + 1);
        }

        // Bottom separator
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.2);
        doc.line(MARGIN + 1.5, y + rowHeight, MARGIN + CONTENT_W, y + rowHeight);

        y += rowHeight;

        // New page if close to bottom
        if (y > PAGE_H - 24) {
            doc.addPage();
            y = 14;
        }
        });
    });

    // ── FOOTER ────────────────────────────────────────────────────────────────
    const footerY = PAGE_H - 10;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, footerY - 3, PAGE_W - MARGIN, footerY - 3);

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    const now = new Date();
    doc.text(
        `Generated on ${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
        MARGIN,
        footerY,
    );
    doc.text('Enjoy Triathlon', PAGE_W - MARGIN, footerY, { align: 'right' });

    // ── SAVE ─────────────────────────────────────────────────────────────────
    const fileName = `training-plan-${weekData.weekStart}.pdf`;
    doc.save(fileName);
}
